<template>
  <router-link
    v-if="tenderId"
    :to="`/tender/${tenderId}`"
    :class="`${baseClass} pa-6`"
    class="saved-search-opportunity-card-link"
    target="_blank"
  >
    <div :class="`${baseClass}-main-row d-flex justify-space-between align-start mb-4`">
      <div :class="`${baseClass}-content flex-grow-1`">
        <div :class="`${baseClass}-title-row`">
          <span :class="`${baseClass}-title`">
            {{ row.title || 'Opportunity' }}
          </span>
        </div>

        <saved-search-meta-chips
          :row="row"
          @filter-notice-type="$emit('filter-notice-type', $event)"
        />
      </div>

      <div
        v-if="showClosingHero"
        class="text-right ml-4 flex-shrink-0"
      >
        <div
          :class="`${baseClass}-days-left text-h5 font-weight-bold`"
          :style="closingHeroStyle"
        >
          {{ closingHeroNumber }}
        </div>
        <div
          v-if="closingHeroSublabel"
          :class="`${baseClass}-days-left-label text-body`"
        >
          {{ closingHeroSublabel }}
        </div>
      </div>
    </div>

    <div :class="`${baseClass}-details-container mb-4`">
      <v-row :class="`${baseClass}-details-row`">
        <v-col cols="6" :class="`${baseClass}-detail-col`">
          <div :class="`${baseClass}-detail-label text-body mb-1`">Buyer</div>
          <div :class="`${baseClass}-detail-value text-body-2 font-weight-medium`">
            <router-link
              v-if="row.publisher_id"
              :to="`/entity/${row.publisher_id}`"
              :class="`${baseClass}-detail-link text-decoration-none`"
              target="_blank"
              @click.stop
            >
              {{ publisherName }}
            </router-link>
            <span v-else>{{ publisherName }}</span>
          </div>
        </v-col>

        <v-col cols="6" :class="`${baseClass}-detail-col`">
          <div :class="`${baseClass}-detail-label text-body mb-1`">Contract Value</div>
          <div :class="`${baseClass}-detail-value text-body-2 font-weight-medium`">
            {{ formatCurrency(row.value || 0) }}
          </div>
        </v-col>

        <v-col cols="6" :class="`${baseClass}-detail-col`">
          <div :class="`${baseClass}-detail-label text-body mb-1`">Published</div>
          <div :class="`${baseClass}-detail-value text-body-2 font-weight-medium`">
            {{ publishedDisplay }}
          </div>
        </v-col>

        <v-col cols="6" :class="`${baseClass}-detail-col`">
          <div :class="`${baseClass}-detail-label text-body mb-1`">Closes</div>
          <div :class="`${baseClass}-detail-value text-body-2 font-weight-medium`">
            {{ closesDisplay }}
          </div>
        </v-col>
      </v-row>
    </div>

    <div
      v-if="$slots.actions"
      :class="`${baseClass}-actions d-flex justify-end`"
      @click.stop.prevent
    >
      <slot name="actions" :row="row" />
    </div>
  </router-link>
</template>

<script>
import SavedSearchMetaChips from './SavedSearchMetaChips.vue';
import { formattedCurrency, formatDateReadable } from '../Utils.js';

export default {
  name: 'SavedSearchOpportunityCard',

  components: {
    SavedSearchMetaChips,
  },

  props: {
    row: {
      type: Object,
      required: true,
    },
    baseClass: {
      type: String,
      default: 'saved-search-opportunity-card',
    },
  },

  computed: {
    tenderId() {
      return this.row?.tender_id || this.row?.id || null;
    },

    publisherName() {
      return this.row?.publisher_name || this.row?.publisher || '—';
    },

    publishedDisplay() {
      return this.row?.published_date
        ? formatDateReadable(this.row.published_date)
        : '—';
    },

    closesDate() {
      return this.row?.closing_date || null;
    },

    closesFallbackDate() {
      return (
        this.row?.display_date ||
        this.row?.closing_date ||
        this.row?.published_date ||
        null
      );
    },

    daysUntilClose() {
      if (!this.closesDate) return null;
      return this.getDaysUntilClose(this.closesDate);
    },

    showClosingHero() {
      return this.closesDate != null && this.daysUntilClose != null;
    },

    closingHeroNumber() {
      if (!this.showClosingHero) return '';
      if (this.daysUntilClose < 0) return '0';
      if (this.daysUntilClose === 0) return 'Today';
      return String(this.daysUntilClose);
    },

    closingHeroSublabel() {
      if (!this.showClosingHero) return '';
      if (this.daysUntilClose < 0) return 'Expired';
      if (this.daysUntilClose === 0) return '';
      if (this.daysUntilClose === 1) return 'day left';
      return 'days left';
    },

    closingHeroStyle() {
      if (!this.showClosingHero) return {};
      return { color: this.getClosingDateColor(this.closesDate) };
    },

    closesDisplay() {
      if (this.closesDate) {
        return formatDateReadable(this.closesDate);
      }

      if (this.closesFallbackDate) {
        return formatDateReadable(this.closesFallbackDate);
      }

      return '—';
    },
  },

  methods: {
    formatCurrency(value) {
      return formattedCurrency(value);
    },

    getDaysUntilClose(closingDate) {
      if (!closingDate) return null;
      try {
        const closeDate = new Date(closingDate);
        closeDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffTime = closeDate - today;
        return Math.round(diffTime / (1000 * 60 * 60 * 24));
      } catch (e) {
        return null;
      }
    },

    getClosingDateColor(closingDate) {
      const days = this.getDaysUntilClose(closingDate);
      if (days == null) return '#4b5563';
      if (days <= 7) return '#dc2626';
      if (days <= 14) return '#d97706';
      return '#059669';
    },
  },
};
</script>

<style scoped>
.saved-search-opportunity-card-link {
  display: block;
  text-decoration: none;
  color: inherit;
  background-color: #f9fafb;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.saved-search-opportunity-card-link:hover {
  background-color: #f3f4f6;
}

.saved-search-opportunity-card-title-row {
  margin-bottom: 4px;
}

.saved-search-opportunity-card-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #1f2937;
}

.saved-search-opportunity-card-days-left-label {
  color: #6b7280;
  font-size: 0.875rem;
}

.saved-search-opportunity-card-details-container {
  margin-top: 12px;
}

.saved-search-opportunity-card-detail-link {
  color: inherit;
}

.saved-search-opportunity-card-actions {
  margin-top: 12px;
  gap: 12px;
}

/* Four equal columns on one row */
@media (min-width: 960px) {
  .saved-search-opportunity-card-details-row {
    flex-wrap: nowrap;
  }

  .saved-search-opportunity-card-details-row > .saved-search-opportunity-card-detail-col {
    flex: 0 0 25%;
    max-width: 25%;
  }
}
</style>
